import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, CheckCircle, AlertCircle, Edit, X, Plus, Trash2 } from 'lucide-react';
import studyAbroadService from '../../services/studyAbroadService';

const AcademicHistorySection = ({ profile, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    highestQualification: '',
    institutions: [],
    academicStrengths: '',
    academicChallenges: '',
    hasAcademicGaps: false,
    academicGapExplanation: '',
    englishProficiency: {
      testType: '',
      score: '',
      testDate: '',
      planToTakeTest: false
    }
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (profile?.academicHistory) {
      const history = profile.academicHistory;
      const hasData = history.highestQualification || (history.institutions && history.institutions.length > 0);
      
      setFormData({
        highestQualification: history.highestQualification || '',
        institutions: history.institutions && history.institutions.length > 0 ? history.institutions : [],
        academicStrengths: history.academicStrengths || '',
        academicChallenges: history.academicChallenges || '',
        hasAcademicGaps: history.hasAcademicGaps || false,
        academicGapExplanation: history.academicGapExplanation || '',
        englishProficiency: {
          testType: history.englishProficiency?.testType || '',
          score: history.englishProficiency?.score || '',
          testDate: history.englishProficiency?.testDate ? history.englishProficiency.testDate.split('T')[0] : '',
          planToTakeTest: history.englishProficiency?.planToTakeTest || false
        }
      });
      
      setIsEditing(!hasData);
    } else {
      setIsEditing(true);
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEnglishProficiencyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      englishProficiency: {
        ...prev.englishProficiency,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleInstitutionChange = (index, field, value) => {
    const updatedInstitutions = [...formData.institutions];
    updatedInstitutions[index] = {
      ...updatedInstitutions[index],
      [field]: field === 'isCurrentlyStudying' ? value : value
    };
    setFormData(prev => ({
      ...prev,
      institutions: updatedInstitutions
    }));
  };

  const addInstitution = () => {
    setFormData(prev => ({
      ...prev,
      institutions: [
        ...prev.institutions,
        {
          name: '',
          country: '',
          fieldOfStudy: '',
          qualificationAwarded: '',
          startDate: '',
          endDate: '',
          grade: '',
          gradeSystem: '',
          isCurrentlyStudying: false
        }
      ]
    }));
  };

  const removeInstitution = (index) => {
    setFormData(prev => ({
      ...prev,
      institutions: prev.institutions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await studyAbroadService.updateAcademicHistory(formData);
      setSuccessMessage('Academic history saved successfully!');
      setIsEditing(false);
      if (onRefresh) onRefresh();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to save academic history');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profile?.academicHistory) {
      const history = profile.academicHistory;
      setFormData({
        highestQualification: history.highestQualification || '',
        institutions: history.institutions || [],
        academicStrengths: history.academicStrengths || '',
        academicChallenges: history.academicChallenges || '',
        hasAcademicGaps: history.hasAcademicGaps || false,
        academicGapExplanation: history.academicGapExplanation || '',
        englishProficiency: {
          testType: history.englishProficiency?.testType || '',
          score: history.englishProficiency?.score || '',
          testDate: history.englishProficiency?.testDate ? history.englishProficiency.testDate.split('T')[0] : '',
          planToTakeTest: history.englishProficiency?.planToTakeTest || false
        }
      });
    }
    setIsEditing(false);
    setErrorMessage('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatQualification = (qual) => {
    if (!qual) return 'Not provided';
    return qual.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header with Edit Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#202356] mb-2">Academic History</h1>
            <p className="text-gray-600">
              {isEditing ? 'Add your educational background and qualifications' : 'Your educational background'}
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 border border-[#202356] text-[#202356] rounded-lg font-medium hover:bg-[#202356] hover:text-white transition-colors"
            >
              <Edit size={18} />
              Edit
            </button>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-green-700">{successMessage}</p>
          </motion.div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </motion.div>
        )}

        {/* View Mode */}
        {!isEditing ? (
          <div className="space-y-6">
            {/* Highest Qualification */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Highest Qualification</h2>
              <p className="text-base font-medium text-gray-900">{formatQualification(formData.highestQualification)}</p>
            </div>

            {/* Institutions */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Educational Institutions</h2>
              {formData.institutions && formData.institutions.length > 0 ? (
                <div className="space-y-6">
                  {formData.institutions.map((institution, index) => (
                    <div key={index} className="pb-6 border-b border-gray-200 last:border-b-0 last:pb-0">
                      <h3 className="font-semibold text-gray-900 mb-3">{institution.name || 'Institution ' + (index + 1)}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Country</p>
                          <p className="text-base font-medium text-gray-900">{institution.country || 'Not provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Field of Study</p>
                          <p className="text-base font-medium text-gray-900">{institution.fieldOfStudy || 'Not provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Qualification</p>
                          <p className="text-base font-medium text-gray-900">{institution.qualificationAwarded || 'Not provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Grade</p>
                          <p className="text-base font-medium text-gray-900">{institution.grade || 'Not provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Duration</p>
                          <p className="text-base font-medium text-gray-900">
                            {formatDate(institution.startDate)} - {institution.isCurrentlyStudying ? 'Present' : formatDate(institution.endDate)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No institutions added yet</p>
              )}
            </div>

            {/* English Proficiency */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">English Proficiency</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Test Type</p>
                  <p className="text-base font-medium text-gray-900">{formData.englishProficiency.testType || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Score</p>
                  <p className="text-base font-medium text-gray-900">{formData.englishProficiency.score || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Test Date</p>
                  <p className="text-base font-medium text-gray-900">{formatDate(formData.englishProficiency.testDate)}</p>
                </div>
              </div>
            </div>

          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Highest Qualification */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Highest Qualification</h2>
              <select
                name="highestQualification"
                value={formData.highestQualification}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              >
                <option value="">Select qualification</option>
                <option value="high_school">High School</option>
                <option value="diploma">Diploma</option>
                <option value="bachelors">Bachelor's Degree</option>
                <option value="masters">Master's Degree</option>
                <option value="doctorate">Doctorate</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Institutions */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Educational Institutions</h2>
                <button
                  type="button"
                  onClick={addInstitution}
                  className="flex items-center gap-2 px-4 py-2 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors"
                >
                  <Plus size={18} />
                  Add Institution
                </button>
              </div>

              {formData.institutions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No institutions added yet. Click "Add Institution" to begin.</p>
              ) : (
                <div className="space-y-6">
                  {formData.institutions.map((institution, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg relative">
                      <button
                        type="button"
                        onClick={() => removeInstitution(index)}
                        className="absolute top-4 right-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>

                      <h3 className="font-semibold text-gray-900 mb-4">Institution {index + 1}</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Institution Name *
                          </label>
                          <input
                            type="text"
                            value={institution.name}
                            onChange={(e) => handleInstitutionChange(index, 'name', e.target.value)}
                            required
                            placeholder="e.g., Harvard University"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Country *
                          </label>
                          <input
                            type="text"
                            value={institution.country}
                            onChange={(e) => handleInstitutionChange(index, 'country', e.target.value)}
                            required
                            placeholder="e.g., United States"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Field of Study *
                          </label>
                          <input
                            type="text"
                            value={institution.fieldOfStudy}
                            onChange={(e) => handleInstitutionChange(index, 'fieldOfStudy', e.target.value)}
                            required
                            placeholder="e.g., Computer Science"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Qualification Awarded *
                          </label>
                          <input
                            type="text"
                            value={institution.qualificationAwarded}
                            onChange={(e) => handleInstitutionChange(index, 'qualificationAwarded', e.target.value)}
                            required
                            placeholder="e.g., Bachelor of Science"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Grade/GPA
                          </label>
                          <input
                            type="text"
                            value={institution.grade}
                            onChange={(e) => handleInstitutionChange(index, 'grade', e.target.value)}
                            placeholder="e.g., 3.8/4.0"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Start Date *
                          </label>
                          <input
                            type="date"
                            value={institution.startDate}
                            onChange={(e) => handleInstitutionChange(index, 'startDate', e.target.value)}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            End Date {!institution.isCurrentlyStudying && '*'}
                          </label>
                          <input
                            type="date"
                            value={institution.endDate}
                            onChange={(e) => handleInstitutionChange(index, 'endDate', e.target.value)}
                            required={!institution.isCurrentlyStudying}
                            disabled={institution.isCurrentlyStudying}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356] disabled:bg-gray-100"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={institution.isCurrentlyStudying}
                              onChange={(e) => handleInstitutionChange(index, 'isCurrentlyStudying', e.target.checked)}
                              className="w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                            />
                            <span className="text-sm text-gray-700">I am currently studying here</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* English Proficiency */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">English Proficiency</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Test Type
                  </label>
                  <select
                    name="testType"
                    value={formData.englishProficiency.testType}
                    onChange={handleEnglishProficiencyChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  >
                    <option value="">Select test type</option>
                    <option value="IELTS">IELTS</option>
                    <option value="TOEFL">TOEFL</option>
                    <option value="PTE">PTE</option>
                    <option value="Duolingo">Duolingo</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Score
                  </label>
                  <input
                    type="text"
                    name="score"
                    value={formData.englishProficiency.score}
                    onChange={handleEnglishProficiencyChange}
                    placeholder="e.g., 7.5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Test Date
                  </label>
                  <input
                    type="date"
                    name="testDate"
                    value={formData.englishProficiency.testDate}
                    onChange={handleEnglishProficiencyChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="planToTakeTest"
                      checked={formData.englishProficiency.planToTakeTest}
                      onChange={handleEnglishProficiencyChange}
                      className="w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                    />
                    <span className="text-sm text-gray-700">I plan to take the test</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X size={20} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default AcademicHistorySection;
